import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { KTSVG } from '../../../../../_mexablock/helpers';
import { searchICCID } from '../../../../service/inventario/productos';

type Props = {
    show: boolean;
    handleClose: (updateGrid: boolean) => void;
};

type ICCIDResult = {
    iccid: string;
    ubicacion: string;
    estatus: string;
    estatus_id: number;
    ubicacion_origen: string;

};

const getBadgeClassById = (id: number) => {
    switch (id) {
        case 9:  // Disponible
        case 3:  // Exitoso
            return 'badge-light-success';
        case 10: // Enviada
            return 'badge-light-primary';
        case 7:  // Activada
            return 'badge-light-info';
        case 8:  // Portada
        case 1:  // Pendiente
        case 2:  // En proceso
            return 'badge-light-warning';
        case 11: // Asignada
        case 13: // En almacen
            return 'badge-light-dark';
        case 16: // Suspendida
        case 17: // Cancelada
        case 4:  // Error
            return 'badge-light-danger';
        default:
            // Para Devuelto, Agotado, Recolectado, etc.
            return 'badge-light-secondary';
    }
};

const SearchICCID: React.FC<Props> = ({ show, handleClose }) => {
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [results, setResults] = useState<ICCIDResult[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const handleSearch = () => {
        setErrorMessage('');
        setResults([]);
        if (!searchValue.trim()) {
            setErrorMessage('Por favor ingresa un ICCID para buscar.');
            return;
        }

        if (searchValue.length < 19) {
            setErrorMessage('El ICCID debe tener al menos 19 dígitos.');
            return;
        }

        setLoading(true);

        searchICCID(searchValue)
            .then((res) => {
                if (res) {
                    const estatusId = res.status?.id || 0;

                    // Aplicamos la regla: Si el estatus es 11 (Asignada), forzamos la ubicación
                    const ubicacionFinal = estatusId === 11
                        ? 'Fuera de almacen'
                        : (res.package?.location || 'Sin ubicación asignada');

                    const mappedResult = {
                        iccid: res.iccid || 'N/A',
                        ubicacion: ubicacionFinal,
                        estatus: res.status?.name || 'Estatus desconocido',
                        estatus_id: res.status?.id || 0,
                        ubicacion_origen: res.package?.location || 'Sin ubicación asignada',
                    };

                    setResults([mappedResult]);
                } else {
                    setResults([]);
                    setErrorMessage('No se encontró información para este ICCID.');
                }
            })
            .catch((err) => {
                setErrorMessage(
                    err?.response?.data?.message || 'Ocurrió un error al buscar el ICCID.'
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onClose = () => {
        setSearchValue('');
        setResults([]);
        setErrorMessage('');
        handleClose(false);
    };

    return (
        <Modal
            id="kt_modal_search_iccid"
            tabIndex={-1}
            aria-hidden="true"
            dialogClassName="modal-dialog modal-dialog-centered mw-600px"
            show={show}
            onHide={onClose}
        >
            <div className="modal-content">
                <div className="modal-header pb-0 border-0 justify-content-end">
                    <div
                        className="btn btn-sm btn-icon btn-active-color-primary"
                        onClick={onClose}
                    >
                        <KTSVG
                            className="svg-icon-2"
                            path="/media/icons/duotune/arrows/arr061.svg"
                        />
                    </div>
                </div>

                <div className="modal-body scroll-y mx-5 pt-0 pb-15">
                    {/* Título */}
                    <div className="text-center mb-10">
                        <h1 className="mb-3">Búsqueda de ICCID</h1>
                        <div className="text-muted fw-semibold fs-5">
                            Ingresa el código para verificar su ubicación y estatus.
                        </div>
                    </div>

                    {/* Barra de búsqueda */}
                    <div className="d-flex align-items-center mb-10">
                        <div className="position-relative w-100 me-3">
                            <KTSVG
                                path="/media/icons/duotune/general/gen021.svg"
                                className="svg-icon-2 svg-icon-gray-500 position-absolute top-50 translate-middle-y ms-4"
                            />
                            <input
                                type="text"
                                className={`form-control form-control-solid ps-12 ${errorMessage ? 'is-invalid' : ''}`}
                                placeholder="Ej. 8952140000000000000"
                                value={searchValue}
                                maxLength={19}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/\D/g, '');
                                    setSearchValue(numericValue);
                                    if (errorMessage) setErrorMessage(''); // Limpia el error al escribir
                                }}
                            />

                            {/* SOLUCIÓN: Agregamos position-absolute y lo fijamos al 100% (fondo) de su contenedor padre */}
                            <div
                                className="position-absolute text-danger w-100"
                                style={{ top: '100%', left: '0', marginTop: '4px' }}
                            >
                                <span role="alert" className="fs-7 fw-semibold">{errorMessage}</span>
                            </div>

                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            {!loading ? (
                                'Buscar'
                            ) : (
                                <span className="indicator-progress" style={{ display: 'block' }}>
                                    <span className="spinner-border spinner-border-sm align-middle"></span>
                                </span>
                            )}
                        </button>
                    </div>


                    {/* Contenedor de Resultados */}
                    <div className="mb-10">
                        <h5 className="fw-bold text-gray-700 mb-5">Resultados:</h5>

                        {results.length > 0 ? (
                            <div className="d-flex flex-column gap-3">
                                {results.map((item, index) => (
                                    <div
                                        key={index}
                                        className="border border-dashed border-gray-300 rounded px-7 py-5"
                                    >
                                        <div className="d-flex flex-stack mb-3">
                                            <div className="me-3">
                                                <span className="text-muted fs-7 fw-semibold d-block mb-2">ICCID</span>
                                                <span className="text-gray-800 fw-bold fs-5">{item.iccid}</span>
                                            </div>
                                            <div className="text-end">
                                                <span className="text-muted fs-7 fw-semibold d-block mb-2 me-3">Estatus</span>
                                                <span className={`badge ${getBadgeClassById(item.estatus_id)} fw-bold text-center d-inline-block`}>
                                                    {item.estatus}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted fs-7 fw-semibold me-2">Ubicación origen:</span>
                                            <span className="text-gray-800 fw-bold fs-6">{item.ubicacion_origen}</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted fs-7 fw-semibold me-2">Ubicación actual:</span>
                                            <span className="text-gray-800 fw-bold fs-6">{item.ubicacion}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <span className="text-muted fs-6 fw-semibold">
                                    Aún no hay resultados para mostrar.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Botón Cerrar (Footer integrado) */}
                    <div className="text-center pt-5">
                        <button
                            type="button"
                            className="btn btn-light px-10"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export { SearchICCID };